import React, { useCallback, useState } from "react";
import { Button, Flex, ModalV2, Textarea, toast } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import {
  useDenyCanopyPaymentBatchMutation,
  useDenyGpxPaymentBatchMutation,
} from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { usePaymentsContext } from "../../Context/PaymentsContext";
import { triggerPaymentBatchesRefresh } from "../../PaymentBatchesTable/refreshSignal";
import type { UsePaymentBatchDenyParams, UsePaymentBatchDenyResult } from "./types";

/**
 * Hook for denying a payment batch.
 *
 * Returns `denyModal` as a React node (instead of just data/handlers) because the deny flow
 * requires a modal with internal state (textarea for the denial reason). By encapsulating
 * the modal JSX within the hook, consumers can simply render it without managing the modal's
 * open/close state or the textarea value. This keeps the consuming component clean and allows
 * `SlideoutDrawerV2.ComplexFooter` to use button configs directly.
 */
export function usePaymentBatchDeny({
  paymentBatchId,
}: UsePaymentBatchDenyParams): UsePaymentBatchDenyResult {
  const { isGPX } = usePaymentsContext();
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState("");

  const handleGpxError = useCallback(
    (err: Error) => {
      addError(formatError(err));
      toast.error(err.message);
      setShowReturnModal(false);
      setReturnReason("");
    },
    [setShowReturnModal, setReturnReason],
  );

  const handleGpxCompleted = useCallback(() => {
    setShowReturnModal(false);
    setReturnReason("");
    triggerPaymentBatchesRefresh();
  }, [setShowReturnModal, setReturnReason]);

  const [denyCanopyPaymentBatch, { loading: canopyLoading }] =
    useDenyCanopyPaymentBatchMutation({
      context: clientContext(),
      onError: (err: Error) => {
        addError(formatError(err));
        toast.error(err.message);
        setShowReturnModal(false);
        setReturnReason("");
      },
      onCompleted: () => {
        setShowReturnModal(false);
        setReturnReason("");
        triggerPaymentBatchesRefresh();
      },
    });

  const [denyGPXPaymentBatch, { loading: gpxLoading }] = useDenyGpxPaymentBatchMutation({
    context: clientContext(),
    onError: handleGpxError,
    onCompleted: handleGpxCompleted,
  });

  const handleDenyClick = useCallback(() => {
    setShowReturnModal(true);
  }, []);

  const handleReturnModalClose = useCallback(() => {
    setShowReturnModal(false);
    setReturnReason("");
  }, []);

  const handleReturnSubmit = useCallback(() => {
    if (!returnReason.trim()) return;

    if (isGPX) {
      void denyGPXPaymentBatch({
        variables: { paymentBatchId, reason: returnReason.trim() },
      });
    } else {
      void denyCanopyPaymentBatch({
        variables: { paymentBatchId, reason: returnReason.trim() },
      });
    }
  }, [isGPX, denyGPXPaymentBatch, denyCanopyPaymentBatch, paymentBatchId, returnReason]);

  const loading = isGPX ? gpxLoading : canopyLoading;

  const denyModal = (
    <ModalV2
      show={showReturnModal}
      onHide={handleReturnModalClose}
      title="Return reason"
      aria-describedby="deny-reason-description"
    >
      <Flex flexDirection="column" gap={20}>
        <div id="deny-reason-description">
          <Textarea
            size="small"
            value={returnReason}
            onChange={(e) => setReturnReason(e.target.value)}
            placeholder="Please add the reason for denying the payment batch"
            isFullWidth
            disabled={loading}
          />
        </div>
        <Flex flexDirection="row" justifyContent="flex-end" gap={12}>
          <Button variant="neutral" onClick={handleReturnModalClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleReturnSubmit}
            disabled={!returnReason.trim() || loading}
            isLoading={loading}
          >
            Submit reason
          </Button>
        </Flex>
      </Flex>
    </ModalV2>
  );

  return { handleDenyClick, loading, denyModal };
}
